import { useEffect } from 'react'
import { Loader } from '@/components/ui/loader'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { INTEGRATIONS, getIntegrationSyncConfig } from '@super/shared-types'
import type { VendorCatalogClick, VendorConnectorProps } from './types'

const ONBOARDING_RETURN_PATH = '/onboarding/integrations'

function launchInstallFlow(mode: 'onboarding' | 'settings') {
  const returnPath = mode === 'onboarding' ? ONBOARDING_RETURN_PATH : undefined
  window.location.href = integrationsClient.buildium.getInstallUrl(returnPath)
}

/**
 * Buildium catalog click. Fires OAuth immediately. Before redirecting, tears
 * down any scheduled listing-URL sync — Buildium syncs LISTINGS natively so
 * the URL-based fallback would otherwise double-sync. Same pre-connect step
 * that the Settings index handler was doing inline.
 */
export const buildiumCatalogClick: VendorCatalogClick = async ({
  mode,
  isSuperAdmin,
  currentCompanyId,
}) => {
  const syncConfig = getIntegrationSyncConfig(INTEGRATIONS.BUILDIUM.TYPE)
  if (syncConfig?.supportsSync && syncConfig.syncTypes.includes('LISTINGS')) {
    try {
      const existingUrlSync =
        isSuperAdmin && currentCompanyId
          ? await knowledgeApiClient.getAdminListingUrlSyncStatus(
              currentCompanyId,
            )
          : await knowledgeApiClient.getListingUrlSyncStatus()
      if (existingUrlSync) {
        if (isSuperAdmin && currentCompanyId) {
          await knowledgeApiClient.removeAdminListingUrlSync(currentCompanyId)
        } else {
          await knowledgeApiClient.removeListingUrlSync()
        }
      }
    } catch (error) {
      console.warn(
        'Buildium pre-connect: failed to clean up existing URL sync',
        error,
      )
    }
  }
  launchInstallFlow(mode)
}

/**
 * Sub-page: reached only via deep-link (user opens the URL directly).
 * Auto-launches OAuth so the behavior matches the catalog-click path.
 */
export function BuildiumConnector({ mode }: VendorConnectorProps) {
  useEffect(() => {
    launchInstallFlow(mode)
  }, [mode])

  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <Loader text={`Redirecting to ${INTEGRATIONS.BUILDIUM.NAME}…`} />
    </div>
  )
}
